import { supabase } from "@/integrations/supabase/client";
import { getInstalledVersionSync } from "@/lib/version";
import { isDebugLoggingEnabled } from "@/lib/debug-logging";
import { getBreadcrumbs, maskObject } from "@/lib/breadcrumbs";

// Best-effort parse of userAgent for Android device model, Android version,
// and browser/webview name+version. Pure string parsing — no network calls.
function parseUserAgent(ua: string | null | undefined) {
  const out: {
    device_model: string | null;
    android_version: string | null;
    os: string | null;
    os_version: string | null;
    browser: string | null;
    browser_version: string | null;
    is_webview: boolean;
  } = {
    device_model: null,
    android_version: null,
    os: null,
    os_version: null,
    browser: null,
    browser_version: null,
    is_webview: false,
  };
  if (!ua) return out;

  const android = ua.match(/Android\s+([\d.]+);?\s*([^;)]*)/i);
  if (android) {
    out.os = "Android";
    out.os_version = android[1] ?? null;
    out.android_version = android[1] ?? null;
    // Device model appears after "Build/" separator or as trailing token before ")"
    const modelMatch = ua.match(/;\s*([^;)]+)\s+Build\//);
    if (modelMatch) out.device_model = modelMatch[1].trim();
    else {
      const tail = ua.match(/Android\s+[\d.]+;\s*[a-z-]+;\s*([^;)]+)\)/i);
      if (tail) out.device_model = tail[1].trim();
    }
  } else if (/iPhone|iPad|iPod/i.test(ua)) {
    out.os = "iOS";
    const iv = ua.match(/OS\s+([\d_]+)/);
    if (iv) out.os_version = iv[1].replace(/_/g, ".");
  } else if (/Windows NT/i.test(ua)) {
    out.os = "Windows";
    const wv = ua.match(/Windows NT\s+([\d.]+)/);
    if (wv) out.os_version = wv[1];
  } else if (/Mac OS X/i.test(ua)) {
    out.os = "macOS";
    const mv = ua.match(/Mac OS X\s+([\d_.]+)/);
    if (mv) out.os_version = mv[1].replace(/_/g, ".");
  }

  // WebView detection (Android WebView usually contains "; wv)")
  out.is_webview = /;\s*wv\)/i.test(ua) || /Version\/[\d.]+\s+Chrome\/[\d.]+\s+Mobile/i.test(ua) && /;\s*wv/i.test(ua);

  const browsers: Array<[string, RegExp]> = [
    ["Edge", /Edg(?:e|iOS|A)?\/([\d.]+)/],
    ["Opera", /(?:OPR|Opera)\/([\d.]+)/],
    ["Firefox", /Firefox\/([\d.]+)/],
    ["Samsung Internet", /SamsungBrowser\/([\d.]+)/],
    ["Chrome", /Chrom(?:e|ium)\/([\d.]+)/],
    ["Safari", /Version\/([\d.]+).*Safari/],
  ];
  for (const [name, re] of browsers) {
    const m = ua.match(re);
    if (m) {
      out.browser = out.is_webview && name === "Chrome" ? "Android WebView" : name;
      out.browser_version = m[1];
      break;
    }
  }
  return out;
}

export type ErrorSeverity = "info" | "warning" | "error" | "critical";
export type ErrorCategory =
  | "frontend"
  | "backend"
  | "otp"
  | "deposit"
  | "bet"
  | "wallet"
  | "result"
  | "report";

const CRITICAL_CATEGORIES: ErrorCategory[] = ["otp", "deposit", "bet", "wallet", "result"];

export function isCriticalCategory(c: string | null | undefined) {
  return CRITICAL_CATEGORIES.includes((c ?? "") as ErrorCategory);
}

type LogInput = {
  message: string;
  stack?: string | null;
  page?: string | null;
  severity?: ErrorSeverity;
  category?: ErrorCategory;
  source?: "frontend" | "backend";
  meta?: Record<string, unknown>;
  screenshot_path?: string | null;
  name?: string | null;
  filename?: string | null;
  lineno?: number | null;
  colno?: number | null;
};

// Prevent recursive/duplicate loops
const recent = new Map<string, number>();
function isDup(key: string) {
  const now = Date.now();
  const last = recent.get(key) ?? 0;
  if (now - last < 5000) return true;
  recent.set(key, now);
  if (recent.size > 50) {
    for (const [k, t] of recent) if (now - t > 30000) recent.delete(k);
  }
  return false;
}

// Parse first frame of a stack trace to extract file, line, column
function parseFirstFrame(stack?: string | null): { filename?: string; lineno?: number; colno?: number } {
  if (!stack) return {};
  const lines = stack.split("\n");
  for (const line of lines) {
    // Chrome:  "    at fn (https://host/path/file.tsx:12:34)"
    // Firefox: "fn@https://host/path/file.tsx:12:34"
    const m = line.match(/(?:https?:\/\/[^\s)]+|\/[^\s)]+):(\d+):(\d+)/);
    if (m) {
      return { filename: m[0].replace(/:(\d+):(\d+)$/, ""), lineno: Number(m[1]), colno: Number(m[2]) };
    }
  }
  return {};
}

// Best-effort browser-side symbolication of a minified stack using source maps.
// Runs only on the error path (never on hot render paths). Guarded by a timeout
// so a slow/missing .map file cannot delay error persistence.
async function symbolicateStack(stack?: string | null): Promise<{
  symbolicated_stack: string | null;
  frames: Array<{ fileName?: string; lineNumber?: number; columnNumber?: number; functionName?: string }> | null;
  first_frame: { filename?: string; lineno?: number; colno?: number; functionName?: string } | null;
  source_available: boolean;
  source_reason: string;
}> {
  if (!stack) {
    return { symbolicated_stack: null, frames: null, first_frame: null, source_available: false, source_reason: "no_stack" };
  }
  try {
    const StackTrace = (await import("stacktrace-js")).default;
    const err: any = new Error();
    err.stack = stack;
    const framesPromise = StackTrace.fromError(err, { offline: false });
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("symbolicate_timeout")), 4000)
    );
    const frames: any[] = await Promise.race([framesPromise, timeoutPromise]);
    if (!frames || frames.length === 0) {
      return { symbolicated_stack: null, frames: null, first_frame: null, source_available: false, source_reason: "no_frames_resolved" };
    }
    const pretty = frames
      .map((f) => `    at ${f.functionName ?? "<anonymous>"} (${f.fileName ?? "?"}:${f.lineNumber ?? "?"}:${f.columnNumber ?? "?"})`)
      .join("\n");
    const f0 = frames[0];
    const first = {
      filename: f0?.fileName ?? undefined,
      lineno: typeof f0?.lineNumber === "number" ? f0.lineNumber : undefined,
      colno: typeof f0?.columnNumber === "number" ? f0.columnNumber : undefined,
      functionName: f0?.functionName ?? undefined,
    };
    const slim = frames.map((f) => ({
      fileName: f.fileName,
      lineNumber: f.lineNumber,
      columnNumber: f.columnNumber,
      functionName: f.functionName,
    }));
    return { symbolicated_stack: pretty, frames: slim, first_frame: first, source_available: true, source_reason: "ok" };
  } catch (e: any) {
    const msg = String(e?.message ?? e ?? "");
    let reason = "symbolicate_failed";
    if (msg.includes("timeout")) reason = "symbolicate_timeout";
    else if (/404|not found/i.test(msg)) reason = "source_map_missing";
    else if (/cors|cross-origin/i.test(msg)) reason = "source_map_cors_blocked";
    return { symbolicated_stack: null, frames: null, first_frame: null, source_available: false, source_reason: reason };
  }
}

function explainScriptError(message: string, hasStack: boolean, hasFilename: boolean): string | null {
  const m = message.toLowerCase().trim();
  const generic = m === "script error." || m === "script error";
  if (!generic && (hasStack || hasFilename)) return null;
  return (
    "Browser masked this as a generic 'Script error' with no file/line/column. " +
    "This happens when the failing script is served without CORS headers or without a crossorigin=\"anonymous\" attribute (common inside Android WebView / Capacitor, and for third-party scripts). " +
    "Source maps also cannot be resolved for the true frame in this case. " +
    "To capture the real error: reproduce the issue with Debug Logging enabled, or open Chrome DevTools remote debugging on the device."
  );
}

export async function logAppError(input: LogInput) {
  try {
    if (typeof window === "undefined") return;
    const debug = isDebugLoggingEnabled();
    const page = input.page ?? window.location.pathname;
    const key = `${page}|${input.message}`.slice(0, 200);
    // In debug mode, bypass de-duplication so a re-reproduced error is always saved fresh.
    if (!debug && isDup(key)) return;

    let user_id: string | null = null;
    let user_phone: string | null = null;
    try {
      const { data } = await supabase.auth.getUser();
      user_id = data.user?.id ?? null;
      user_phone = data.user?.phone ?? (data.user?.user_metadata as any)?.phone ?? null;
    } catch {}

    const category = input.category ?? "frontend";
    const severity = input.severity ?? (isCriticalCategory(category) ? "critical" : "error");

    // Raw first frame from the minified stack (fallback).
    const parsedRaw = parseFirstFrame(input.stack);

    // Attempt source-map symbolication (best-effort, timeout-guarded).
    const sym = await symbolicateStack(input.stack);

    const filename =
      sym.first_frame?.filename ?? input.filename ?? parsedRaw.filename ?? null;
    const lineno =
      sym.first_frame?.lineno ?? input.lineno ?? parsedRaw.lineno ?? null;
    const colno =
      sym.first_frame?.colno ?? input.colno ?? parsedRaw.colno ?? null;

    const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;
    const uaInfo = parseUserAgent(ua);

    const scriptErrorExplanation = explainScriptError(
      String(input.message ?? ""),
      Boolean(input.stack),
      Boolean(filename)
    );

    const enrichedMeta = {
      ...(input.meta ?? {}),
      name: input.name ?? null,
      filename,
      lineno,
      colno,
      function_name: sym.first_frame?.functionName ?? null,
      raw_filename: input.filename ?? parsedRaw.filename ?? null,
      raw_lineno: input.lineno ?? parsedRaw.lineno ?? null,
      raw_colno: input.colno ?? parsedRaw.colno ?? null,
      symbolicated_stack: sym.symbolicated_stack,
      symbolicated_frames: sym.frames,
      source_maps_available: sym.source_available,
      source_maps_reason: sym.source_reason,
      script_error_explanation: scriptErrorExplanation,
      app_version: getInstalledVersionSync() || "unknown",
      user_agent: ua,
      platform: typeof navigator !== "undefined" ? (navigator as any).platform ?? null : null,
      language: typeof navigator !== "undefined" ? navigator.language : null,
      device_model: uaInfo.device_model,
      android_version: uaInfo.android_version,
      os: uaInfo.os,
      os_version: uaInfo.os_version,
      browser: uaInfo.browser,
      browser_version: uaInfo.browser_version,
      is_webview: uaInfo.is_webview,
      viewport:
        typeof window !== "undefined"
          ? { width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio }
          : null,
      url: typeof window !== "undefined" ? window.location.href : null,
      captured_at: new Date().toISOString(),
      debug_logging: debug,
      network_online: typeof navigator !== "undefined" ? navigator.onLine : null,
      connection: (() => {
        const c: any = typeof navigator !== "undefined" ? (navigator as any).connection : null;
        return c ? { effectiveType: c.effectiveType, downlink: c.downlink, rtt: c.rtt, saveData: c.saveData } : null;
      })(),
      breadcrumbs: maskObject(getBreadcrumbs().slice(-25)),
    };

    // Group identical errors by symbolicated (original) frame when we have it,
    // so the same bug at a stable source location groups across builds.
    const groupKeyRaw = `${input.name ?? ""}|${String(input.message).slice(0, 200)}|${filename ?? ""}:${lineno ?? ""}|${page}`;
    const group_hash = await sha1Hex(groupKeyRaw);

    // Prefer the symbolicated stack in the primary `stack` column so the
    // admin panel and copy-prompt show original source lines by default.
    const finalStack = sym.symbolicated_stack
      ? `${sym.symbolicated_stack}\n\n--- original (minified) ---\n${input.stack ?? ""}`
      : input.stack ?? null;

    await supabase.from("app_errors").insert({
      user_id,
      user_phone,
      page,
      message: String(input.message).slice(0, 2000),
      stack: finalStack ? String(finalStack).slice(0, 12000) : null,
      severity,
      category,
      source: input.source ?? "frontend",
      meta: enrichedMeta as any,
      screenshot_path: input.screenshot_path ?? null,
      group_hash,
    } as any);

  } catch {
    // swallow — never let the logger crash the app
  }
}

async function sha1Hex(input: string): Promise<string> {
  try {
    if (typeof crypto !== "undefined" && crypto.subtle) {
      const buf = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(input));
      return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch {}
  // Fallback: simple hash
  let h = 0;
  for (let i = 0; i < input.length; i++) { h = ((h << 5) - h) + input.charCodeAt(i); h |= 0; }
  return `f${(h >>> 0).toString(16)}`;
}

let installed = false;

// Best-effort: mark same-origin scripts as CORS-anonymous so future window.error
// events include real message/filename/stack instead of the browser-masked
// "Script error." with no details. Same-origin bundles satisfy CORS with no
// server change; cross-origin scripts (fonts, analytics) are left untouched.
function armSameOriginScriptCors() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const sameOrigin = (src: string) => {
    try { return new URL(src, window.location.href).origin === window.location.origin; }
    catch { return false; }
  };
  const arm = (el: HTMLScriptElement) => {
    if (!el.src || el.crossOrigin) return;
    if (sameOrigin(el.src)) el.crossOrigin = "anonymous";
  };
  try { document.querySelectorAll("script[src]").forEach((s) => arm(s as HTMLScriptElement)); } catch {}
  try {
    const mo = new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((n) => {
          if ((n as HTMLElement).tagName === "SCRIPT") arm(n as HTMLScriptElement);
        });
      }
    });
    mo.observe(document.documentElement, { subtree: true, childList: true });
  } catch {}
}

// True when the browser masked the error and no meaningful detail survives —
// logging these floods the admin panel with un-actionable "Script error." rows.
function isMaskedEmptyScriptError(ev: ErrorEvent): boolean {
  const msg = String(ev.message ?? "").trim().toLowerCase();
  const generic = msg === "script error." || msg === "script error" || msg === "";
  const noDetails = !ev.error && !ev.filename && !ev.lineno && !ev.colno;
  return generic && noDetails;
}

export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  armSameOriginScriptCors();

  window.addEventListener("error", (ev) => {
    if (isMaskedEmptyScriptError(ev)) return; // drop pure noise
    const err = ev.error;
    logAppError({
      message: err?.message ?? String(ev.message ?? "Unknown error"),
      stack: err?.stack,
      name: err?.name ?? "Error",
      filename: ev.filename ?? null,
      lineno: ev.lineno ?? null,
      colno: ev.colno ?? null,
      severity: "error",
      category: "frontend",
      meta: { kind: "window.error" },
    });
    reportDiagnostic("unhandled_error", err ?? ev.message, { filename: ev.filename, lineno: ev.lineno });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const reason: any = ev.reason;
    logAppError({
      message: reason?.message ?? String(reason ?? "Unhandled promise rejection"),
      stack: reason?.stack,
      name: reason?.name ?? "UnhandledRejection",
      severity: "error",
      category: "frontend",
      meta: { kind: "unhandledrejection" },
    });
    reportDiagnostic("unhandled_rejection", reason, {});
  });
}

// Mirrors global failures into the diagnostics contract. Best-effort only —
// a diagnostics failure can never affect app behaviour.
function reportDiagnostic(code: string, error: unknown, metadata: Record<string, unknown>) {
  void (async () => {
    try {
      const { report } = await import("@/lib/diagnostics/report");
      const msg = String((error as any)?.message ?? error ?? "").toLowerCase();
      const isPlugin = msg.includes("not implemented on") || msg.includes("plugin is not");
      report({
        category: isPlugin ? "startup" : "unknown",
        code: isPlugin ? "plugin_unavailable" : code,
        error,
        severity: "error",
        metadata,
      });
    } catch {
      /* ignore */
    }
  })();
}


